// src/modules/assetManagement/management/AssetUpload.tsx
// Upload (asset ingestion): drop files, optionally tag and link them, watch progress, retry or escalate failures.
import React, { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AlertCircle, CheckCircle2, CloudUpload, Eye, FileAudio, FileImage, FileText, FileVideo,
  Headphones, History, Loader2, RotateCcw, Tag, Trash2, X,
} from "lucide-react";
import { assetApi } from "../assetApi";
import { SupportModal } from "../components/AssetModals";
import EventProductPicker from "../components/EventProductPicker";
import { uncoveredProducts, type LinkSelection } from "../linkRules";
import { Button, Chip } from "../components/ui";
import { useAssetQuery } from "../useAssetQuery";
import { ACCEPTED_EXTENSIONS, ENTITY, formatBytes, MAX_ATTEMPTS, ROUTES } from "../utils";
import type { QueueItem, useUploadQueue } from "./useUploadQueue";

type Queue = ReturnType<typeof useUploadQueue>;

const KIND_ICON = { image: FileImage, video: FileVideo, audio: FileAudio, document: FileText };

const statusLine = (i: QueueItem) => {
  switch (i.status) {
    case "invalid":
      return { text: i.error ?? "Not accepted", className: "text-red-600" };
    case "ready":
      return { text: "Ready to upload", className: "text-slate-500" };
    case "uploading":
      return !i.started
        ? { text: "Waiting…", className: "text-slate-500" }
        : { text: `Uploading ${Math.round(i.progress)}%${i.attempts > 1 ? ` · attempt ${i.attempts} of ${MAX_ATTEMPTS}` : ""}`, className: "text-slate-600" };
    case "processing":
      return { text: "Storing and processing on the server…", className: "text-blue-600" };
    case "done":
      return { text: "Uploaded", className: "text-emerald-600" };
    case "failed":
      return {
        text: `${i.error} · attempt ${i.attempts} of ${MAX_ATTEMPTS}`,
        className: "text-red-600",
      };
  }
};

const AssetUpload: React.FC<{ queue: Queue; onViewLog: () => void }> = ({ queue, onViewLog }) => {
  const { items, addFiles, start, retry, remove, clearFinished, markTicket } = queue;
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const [dragging, setDragging] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [links, setLinks] = useState<LinkSelection>({ eventIds: [], productIds: [] });
  const [supportFor, setSupportFor] = useState<QueueItem | null>(null);
  const { data: events = [] } = useAssetQuery(() => assetApi.listEvents(), []);
  const { data: products = [] } = useAssetQuery(() => assetApi.listProducts(), []);

  const ready = items.filter((i) => i.status === "ready");
  const linksIncomplete = uncoveredProducts(events, links).length > 0;
  const counts = {
    done: items.filter((i) => i.status === "done").length,
    failed: items.filter((i) => i.status === "failed" || i.status === "invalid").length,
    active: items.filter((i) => i.status === "uploading" || i.status === "processing").length,
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/^#/, "").replace(/\s+/g, "-");
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const acceptAttr = Object.keys(ACCEPTED_EXTENSIONS).map((x) => `.${x}`).join(",");

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2 space-y-4 min-w-0">
        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
          className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition ${
            dragging ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50"
          }`}
        >
          <CloudUpload size={36} className={`mx-auto ${dragging ? "text-emerald-600" : "text-slate-400"}`} />
          <p className="mt-3 font-medium text-slate-800">Drag files here, or click to browse</p>
          <p className="text-sm text-slate-500 mt-1">Images, video, audio and documents · up to 10 GB per file</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={acceptAttr}
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {/* Queue */}
        {items.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-slate-100">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{items.length} file{items.length === 1 ? "" : "s"}</span>
                {counts.active > 0 && ` · ${counts.active} in progress`}
                {counts.done > 0 && ` · ${counts.done} uploaded`}
                {counts.failed > 0 && <span className="text-red-600"> · {counts.failed} failed</span>}
              </p>
              <div className="flex gap-2">
                {counts.failed > 0 && (
                  <Button size="sm" variant="ghost" icon={<History size={14} />} onClick={onViewLog}>
                    Upload log
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={clearFinished}>
                  Clear finished
                </Button>
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {items.map((i) => {
                const Icon = KIND_ICON[i.kind];
                const line = statusLine(i);
                const exhausted = i.status === "failed" && i.attempts >= MAX_ATTEMPTS;
                return (
                  <li key={i.key} className="px-5 py-3 flex items-center gap-4">
                    <span
                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        i.status === "failed" || i.status === "invalid"
                          ? "bg-red-50 text-red-500"
                          : i.status === "done"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {i.status === "done" ? (
                        <CheckCircle2 size={18} />
                      ) : i.status === "failed" || i.status === "invalid" ? (
                        <AlertCircle size={18} />
                      ) : i.status === "processing" ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Icon size={18} />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-medium text-slate-800 truncate">{i.file.name}</p>
                        <span className="text-xs text-slate-400 shrink-0">{formatBytes(i.file.size)}</span>
                      </div>
                      <p className={`text-xs mt-0.5 truncate ${line.className}`}>
                        {line.text}
                        {i.ticketRef && <span className="text-slate-500"> · ticket {i.ticketRef}</span>}
                      </p>
                      {(i.status === "uploading" || i.status === "processing") && (
                        <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${i.status === "processing" ? "bg-blue-500 animate-pulse" : "bg-emerald-500"}`}
                            style={{ width: `${i.progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {i.status === "failed" && !exhausted && (
                        <Button size="sm" icon={<RotateCcw size={13} />} onClick={() => retry(i.key)}>
                          Retry
                        </Button>
                      )}
                      {(exhausted || i.status === "invalid") && !i.ticketRef && (
                        <Button size="sm" icon={<Headphones size={13} />} onClick={() => setSupportFor(i)}>
                          Get support
                        </Button>
                      )}
                      {(i.status === "done" || i.status === "failed") && i.assetId && (
                        <Link to={ROUTES.managedAsset(i.assetId)} state={{ trail: [{ label: "Asset Management", to: `${location.pathname}${location.search}` }] }}>
                          <Button size="sm" variant="ghost" icon={<Eye size={13} />}>
                            View
                          </Button>
                        </Link>
                      )}
                      {(i.status === "ready" || i.status === "invalid" || i.status === "failed" || i.status === "done") && (
                        <button
                          type="button"
                          onClick={() => remove(i.key)}
                          className="p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100"
                          aria-label={`Remove ${i.file.name}`}
                          title="Remove from list"
                        >
                          {i.status === "done" ? <X size={15} /> : <Trash2 size={15} />}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Options + start */}
      <aside className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Apply to this upload</h3>
            <p className="text-xs text-slate-500 mt-0.5">Optional. You can also tag and link later.</p>
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
              <Tag size={13} /> Tags
            </label>
            <div className="flex gap-2">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Type a tag and press Enter"
                className="flex-1 min-w-0 px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
              <Button size="sm" onClick={addTag} disabled={!tagInput.trim()}>Add</Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map((t) => (
                  <Chip key={t} className={ENTITY.asset.chip} onRemove={() => setTags(tags.filter((x) => x !== t))}>
                    #{t}
                  </Chip>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Link to</p>
            <EventProductPicker events={events} products={products} value={links} onChange={setLinks} layout="stacked" />
          </div>

          <Button
            variant="primary"
            className="w-full"
            icon={<CloudUpload size={16} />}
            disabled={ready.length === 0 || linksIncomplete}
            title={linksIncomplete ? "Choose an event for each selected product" : undefined}
            onClick={() => start({ tags, eventIds: links.eventIds, productIds: links.productIds })}
          >
            {ready.length > 0 ? `Upload ${ready.length} file${ready.length === 1 ? "" : "s"}` : "Add files to upload"}
          </Button>
        </div>
      </aside>

      {supportFor && (
        <SupportModal
          fileName={supportFor.file.name}
          defaultCategory="Upload failed"
          defaultDescription={`Upload of ${supportFor.file.name} (${formatBytes(supportFor.file.size)}) failed ${supportFor.attempts} time${
            supportFor.attempts === 1 ? "" : "s"
          }.\nLast error: ${supportFor.errorCode} – ${supportFor.error}\nStage: ${supportFor.stage}\n\n`}
          onClose={() => setSupportFor(null)}
          onRaised={(t) => {
            markTicket(supportFor.key, t.ref);
            void assetApi.resolveFileAttempts(supportFor.key, "ticket_raised", t.ref);
          }}
        />
      )}
    </div>
  );
};

export default AssetUpload;